import { useCallback, useRef } from 'react';

// Components
import DisplayError from '../../../components/info/DisplayError';
import ModalUploadDocument, {
  ModalUploadDocumentMethods,
} from './ModalUploadDocument';
import {
  Dialog,
  DisplayInfo,
  Table,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';

// Hooks
import useRequiredFilesColumns from '../hooks/useRequiredFilesColumns';
import { useDeleteFileMutation, useGetRequiredFilesQuery } from '../api';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { RequiredFile } from '../types';

type RequiredFilesTableProps = {
  userId: string | number;
};

// Render
const RequiredFilesTable = ({ userId }: RequiredFilesTableProps) => {
  const { t } = useTranslation();
  const modalUploadDocumentRef = useRef<ModalUploadDocumentMethods>(null);

  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();

  const requirementsData = useGetRequiredFilesQuery(userId, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const handleDestroy = useCallback(
    async (fileId: number) => {
      try {
        await deleteFile(fileId)?.unwrap();
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`views.hiring.requirements.modalDestroy.success`),
        });
        requirementsData?.refetch();
      } catch (_error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`views.hiring.requirements.modalDestroy.failed`),
        });
      }
    },
    [deleteFile, requirementsData, t],
  );

  const handleRequestDestroy = useCallback(
    (id: number) => {
      showDialogConfirm({
        title: t(`views.hiring.requirements.modalDestroy.title`),
        subtitle: t(`views.hiring.requirements.modalDestroy.subtitle`),
        btnClose: {
          text: t(`views.hiring.requirements.modalDestroy.cancel`),
        },
        btnConfirm: {
          text: t(`views.hiring.requirements.modalDestroy.confirm`),
          onConfirm: () => handleDestroy(id),
        },
      });
    },
    [t, handleDestroy],
  );

  const handleRequestUpload = useCallback(
    (props: RequiredFile) => {
      modalUploadDocumentRef?.current?.open(props, userId);
    },
    [userId],
  );

  const handleRequestDownload = useCallback((props: RequiredFile) => {
    if (props?.url) {
      window.open(props?.url, '_blank')?.focus();
    }
  }, []);

  const columns = useRequiredFilesColumns({
    onPressDestroy: handleRequestDestroy,
    onPressUpload: handleRequestUpload,
    onPressDownload: handleRequestDownload,
  });

  return (
    <>
      <Table
        columns={columns}
        data={requirementsData?.data || []}
        isLoadingResults={requirementsData?.isFetching || isDeleting}
        loadingView={{
          title: t(`common.loading.title`),
          subtitle: t(`common.loading.subtitle`),
        }}
        noResultsText={
          requirementsData?.error ? (
            <DisplayError
              typeError={(requirementsData?.error as any)?.status}
            />
          ) : (
            <div className="d-flex flex-column align-items-center no-result">
              <DisplayInfo
                title={t('views.hiring.requirements.noResult')}
                textBody={t('views.hiring.requirements.noResultDescription')}
              />
            </div>
          )
        }
      />
      <Dialog />
      <ModalUploadDocument
        ref={modalUploadDocumentRef}
        onConfirm={requirementsData?.refetch}
      />
    </>
  );
};

export default RequiredFilesTable;
